"use client";
import {useCallback, useState} from "react";
import {
	ClassesByCampusId,
	Class,
} from "../../../../../../lib/legacyApi/fetchClassesForCourseByCampusId";
import CampusSelector from "./CampusSelector";
import {CampusesById} from "../../../../../types/Campus";
import {SetCurrentCampuses} from "./CampusSelectorDropdown";
import Checkbox from "../../../../../_components/forms/Checkbox";
import AvailableSessionsClassList from "./AvailableSessionsClassList";
import OtherLocationsNearYou from "./AvailableSessionsOtherLocations";
import NoSessionsAvailable, {NoSessionsMode} from "./NoSessionsAvailable";
import {SetSelectedDays} from "./DayOfWeekDropdown";
import MobileDuoFilter from "./MobileDuoFilter";
import getOneFromSet from "@/app/util/getOneFromSet";
import {Selection} from "react-aria-components";
import {useCampusContext} from "../../../../../_components/contexts/CampusCookieContext";
import EnrollStatus from "../../../../../types/EnrollStatus";
import RoleRestrictedView from "@/app/_components/layout/RoleRestrictedView";
import AvailableSessionErrorWrapper from "./AvailableSessionErrorWrapper";
import {STUDENT_ROLES} from "@/app/util/userUtils";
import useFakeLoading from "../../../../../util/useFakeLoading";
import NoLocationSelected from "./NoLocationSelected";
import {DayOfWeek} from "@/shared/legacyTypes/fetchClassesTypes";

interface AvailableSessionsProps {
	title: string;
	courseId: string;
	classesByCampus: ClassesByCampusId;
	allCampusesById: CampusesById;
	studentFallbackText?: string;
}

/**
 * Returns a filter function that determines if a class should be shown.
 *
 * @param showWaitlisted Whether to show waitlisted classes.
 */
const shouldShowClass =
	({showWaitlisted}: {showWaitlisted: boolean}) =>
	(myClass: Class) => {
		return (
			myClass.enroll_status === EnrollStatus.OPEN ||
			(myClass.enroll_status === EnrollStatus.WAITLIST && showWaitlisted)
		);
	};

enum ClassListState {
	NO_CAMPUS_SELECTED,
	NO_CLASSES_TO_SHOW,
	SHOW_CLASSES,
	COMING_SOON,
}

export const AvailableSessions = ({
	title,
	courseId,
	classesByCampus,
	allCampusesById,
	studentFallbackText,
}: AvailableSessionsProps) => {
	const {campus, setCampus} = useCampusContext();
	const {triggerLoading, opacityLoadingClassName} = useFakeLoading();
	const currentCampuses = new Set<string>();

	// only set the selected campus to the cookie campus if we have data for it
	if (campus && allCampusesById[campus.campusId]) {
		currentCampuses.add(campus.campusId);
	}
	const setCurrentCampuses: SetCurrentCampuses = useCallback(
		(campuses: Selection) => {
			if (campuses === "all") {
				return;
			}
			const selectedCampus = getOneFromSet(campuses)?.toString();
			if (!selectedCampus) {
				return;
			}
			setCampus(selectedCampus);
			triggerLoading();
		},
		[triggerLoading, setCampus]
	);
	const [daysOfWeek, setDaysOfWeek] = useState(new Set<DayOfWeek>());
	const setSelectedDays = useCallback(
		(days: Set<DayOfWeek>) => {
			setDaysOfWeek(days);
			triggerLoading();
		},
		[triggerLoading]
	) as SetSelectedDays;

	const [currentCampusId] = currentCampuses;

	const classes = classesByCampus[currentCampusId] ?? [];
	const [showWaitlisted, setShowWaitlisted] = useState(true);

	const onChangeShowWaitlisted = useCallback(
		(isSelected: boolean) => {
			setShowWaitlisted(!isSelected);
			triggerLoading();
		},
		[triggerLoading]
	);

	const classesToShow = classes.filter(shouldShowClass({showWaitlisted}));

	if (!classesByCampus) {
		return (
			<NoSessionsAvailable
				noSessionsMode={NoSessionsMode.ANYWHERE}
				courseId={courseId}
				campus={allCampusesById[currentCampusId]}
			/>
		);
	}

	let classListState = ClassListState.SHOW_CLASSES;
	if (!currentCampusId || currentCampuses.size === 0) {
		classListState = ClassListState.NO_CAMPUS_SELECTED;
	} else if (!classesToShow.length && campus?.comingSoon) {
		classListState = ClassListState.COMING_SOON;
	} else if (!classesToShow.length) {
		classListState = ClassListState.NO_CLASSES_TO_SHOW;
	}

	return (
		<div className="flex flex-col gap-8">
			<div className="flex flex-col gap-4 text-center">
				<h2>{title}</h2>
			</div>
			<div className="border-1 border-gray-500">
				<div className="flex items-center justify-between bg-gray-200 p-4 lg:px-8">
					<div className="hidden gap-4 md:flex">
						<CampusSelector
							allCampusesById={allCampusesById}
							currentCampuses={currentCampuses}
							setCurrentCampuses={setCurrentCampuses}
						/>
					</div>
					<MobileDuoFilter
						currentCampuses={currentCampuses}
						setCurrentCampuses={setCurrentCampuses}
						selectedDays={daysOfWeek}
						setSelectedDays={setSelectedDays}
						allCampusesById={allCampusesById}
					/>
					<Checkbox
						className="hidden cursor-pointer sm:flex"
						isSelected={!showWaitlisted}
						onChange={onChangeShowWaitlisted}
					>
						Show Only Open Classes
					</Checkbox>
				</div>
				<RoleRestrictedView
					hiddenForRoles={STUDENT_ROLES}
					fallbackElement={
						<AvailableSessionErrorWrapper>
							<span className="title-h3">{studentFallbackText}</span>
						</AvailableSessionErrorWrapper>
					}
				>
					{classListState === ClassListState.SHOW_CLASSES && (
						<AvailableSessionsClassList
							className={opacityLoadingClassName}
							classes={classesToShow}
							selectedDays={daysOfWeek}
						/>
					)}
					{classListState === ClassListState.NO_CLASSES_TO_SHOW && (
						<NoSessionsAvailable
							className={opacityLoadingClassName}
							noSessionsMode={NoSessionsMode.AT_CAMPUS}
							courseId={courseId}
							campus={allCampusesById[currentCampusId]}
						/>
					)}
					{classListState === ClassListState.NO_CAMPUS_SELECTED && (
						<NoLocationSelected />
					)}
					{classListState === ClassListState.COMING_SOON && (
						<NoSessionsAvailable
							className={opacityLoadingClassName}
							noSessionsMode={NoSessionsMode.COMING_SOON}
							courseId={courseId}
							campus={allCampusesById[currentCampusId]}
						/>
					)}
				</RoleRestrictedView>
			</div>
			<OtherLocationsNearYou
				allCampusesById={allCampusesById}
				currentCampuses={currentCampuses}
				setCurrentCampuses={setCurrentCampuses}
			/>
		</div>
	);
};

export default AvailableSessions;
